import { useRef, useState } from 'react';
import { useGlobalCache } from '../../../utils/useGlobalCache';
import calApi from '../api/calApi';
import { logBookingError } from '../loggingUtils';
import { BookerContextType, CalEventData } from '../types';

export const useFetchCalEvents = (bookerContext: BookerContextType) => {
    const abortControllerRef = useRef<AbortController | null>(null);
    const globalCache = useGlobalCache();
    const [isLoading, setIsLoading] = useState(true);

    const fetchCalEvents = async ({
        organizationId,
        teamId,
    }: {
        organizationId: number;
        teamId: number;
    }) => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }

        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        try {
            const queryKey = ['cal-events', organizationId, teamId];
            const cachedRequest =
                globalCache.get<Promise<CalEventData[]>>(queryKey);
            if (cachedRequest) {
                return await cachedRequest;
            }

            setIsLoading(true);

            const request = calApi.getTeamEvents({ organizationId, teamId });
            globalCache.set(queryKey, request);
            // drop a failed request, otherwise no later mount can retry
            request.catch(() => globalCache.set(queryKey, null));

            return await request;
        } catch (error) {
            // Don't log errors for aborted requests
            if (!abortController.signal.aborted) {
                logBookingError('Failed to fetch cal events', {
                    errorMessage: (error as Error).message,
                    additionalInfoToLog: bookerContext,
                });
            }
        } finally {
            if (!abortController.signal.aborted) {
                setIsLoading(false);
            }
            if (abortControllerRef.current === abortController) {
                abortControllerRef.current = null;
            }
        }
    };

    return { isLoading, fetchCalEvents };
};
